import type { PdaContext, PdaOfflineGrant } from '@fuel-system/contracts';
import { PRODUCT_ACCESS_SERVICES } from '@/features/home/home-model';
import type { PdaSession } from '@/features/auth/session';

export type GrantState = 'NONE' | 'VALID' | 'EXPIRED';

/**
 * Whether the cached grant still allows offline work (fuel-pda.md 13.4): until
 * `expiresAt`, the last online sign-in + 24 hours. A grant whose dates cannot
 * be read counts as expired, and so does any grant while the device clock is
 * earlier than the last server time seen (the clock was set back to stretch
 * the window).
 */
/** Normal clock drift allowed before a device clock counts as set back. */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

export function grantState(
  grant: PdaOfflineGrant | null | undefined,
  now: number,
  /** The latest server time seen in a pull: a device clock before it was set back. */
  lastServerTime?: string | null,
): GrantState {
  if (!grant) return 'NONE';
  const seen = lastServerTime ? Date.parse(lastServerTime) : Number.NaN;
  if (Number.isFinite(seen) && now < seen - CLOCK_SKEW_TOLERANCE_MS) return 'EXPIRED';
  const expiresAt = Date.parse(grant.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return 'EXPIRED';
  return 'VALID';
}

/** Marks a session built on the device from the offline grant. */
export type OfflineSession = PdaSession & { offline: true };

export function isOfflineSession(session: PdaSession | undefined): session is OfflineSession {
  return (session as Partial<OfflineSession> | undefined)?.offline === true;
}

/**
 * The session the screens read while offline: the grant's user, branch and
 * services; the branch name and business date of the last online session of
 * the same branch, and the business date of the cached context.
 */
export function sessionFromGrant(
  grant: PdaOfflineGrant,
  options: {
    locale: 'ar' | 'en';
    now: number;
    lastSession: PdaSession | null | undefined;
    context: PdaContext | null | undefined;
  },
): OfflineSession {
  const { lastSession, context } = options;
  const sameBranch = lastSession?.branch.id === grant.branchId ? lastSession.branch : null;
  const branch =
    sameBranch ??
    (context?.branch.id === grant.branchId
      ? { id: context.branch.id, code: '', name: context.branch.name }
      : { id: grant.branchId, code: '', name: '' });
  const services = new Set(grant.services);
  for (const product of grant.products) services.add(PRODUCT_ACCESS_SERVICES[product]);
  const expiresInMs = Math.max(0, Date.parse(grant.expiresAt) - options.now);
  const businessDate = context?.businessDate ?? lastSession?.businessDate;
  return {
    user: {
      id: grant.userId,
      name: options.locale === 'en' ? grant.nameEn || grant.nameAr : grant.nameAr,
      username: lastSession?.user.id === grant.userId ? lastSession.user.username : '',
    },
    branch,
    services: [...services],
    ...(businessDate ? { businessDate } : {}),
    expiresAt: grant.expiresAt,
    expiresInMs,
    offline: true,
  };
}
